import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    getResourceDetails, 
    getFinancialStats, 
    getActiveResourceInfo,
    updateGestionRate,
    updateVirement,
    getPaidHistory,
    getGestionHistory,
    updateMissionSpecificRate,
    getMonthExpenses,          
    updateExpenseDeduction,
    updateAjustement    
} from '../api/api';
import styles from './AdminLists.module.css';
import MissionDetailsModal from '../components/MissionDetailsModal';
import RateHistoryModal from '../components/RateHistoryModal'; 

const ResourceDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // États existants
    const [activeTab, setActiveTab] = useState('infos');
    const [details, setDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [financials, setFinancials] = useState([]);
    const [activeInfo, setActiveInfo] = useState(null);
    const [loadingFinance, setLoadingFinance] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [editingMissionRate, setEditingMissionRate] = useState(null);

    // États pour la gestion des NDF
    const [expenseModalInfo, setExpenseModalInfo] = useState(null);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // États pour formulaires
    const [showRateForm, setShowRateForm] = useState(false);
    const [rateForm, setRateForm] = useState({ 
        rate: 10.0, 
        month: new Date().getMonth() + 1, 
        year: new Date().getFullYear(),
        comment: "" 
    });

    const [editingVirement, setEditingVirement] = useState(null);
    const [editingAjustement, setEditingAjustement] = useState(null);
    const [selectedMissionId, setSelectedMissionId] = useState(null);

    // États pour Onglet 4
    const [paidHistory, setPaidHistory] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [expandedHistory, setExpandedHistory] = useState({}); 

    // États pour HISTORIQUE TAUX
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [rateHistoryData, setRateHistoryData] = useState([]);
    const [loadingRateHistory, setLoadingRateHistory] = useState(false);

    // Chargement initial
    useEffect(() => {
        const fetchDetails = async () => {
            setLoadingDetails(true);
            try {
                const res = await getResourceDetails(id);
                setDetails(res.data);
            } catch (err) { console.error(err); } finally { setLoadingDetails(false); }
        };
        if (id) fetchDetails();
    }, [id]);

    // Chargement Finance (Onglet 3)
    useEffect(() => {
        if (activeTab === 'finance' && id && financials.length === 0) {
            fetchFinancialData(id);
        }
    }, [activeTab, id]);

    // Chargement Onglet 4
    useEffect(() => {
        if (activeTab === 'history' && id && !paidHistory) {
            fetchHistoryData(id);
        }
    }, [activeTab, id, paidHistory]);

    const fetchFinancialData = async (resourceId) => {
        setLoadingFinance(true);
        try {
            const [financeRes, infoRes] = await Promise.all([
                getFinancialStats(resourceId),
                getActiveResourceInfo(resourceId)
            ]);

            const rawData = financeRes.data || [];
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1;

            const isProduction = process.env.REACT_APP_ENV === 'Production';

            const filteredData = rawData.filter(item => {
                if (isProduction && item.year < 2026) return false;
                if (item.year < currentYear) return true;
                if (item.year === currentYear && item.month < currentMonth) return true;
                return false;
            });

            setFinancials(filteredData);
            setActiveInfo(infoRes.data);
        } catch (err) { console.error(err); } finally { setLoadingFinance(false); }
    };

    const fetchHistoryData = async (resourceId) => {
        setLoadingHistory(true);
        try {
            const res = await getPaidHistory(resourceId);
            setPaidHistory(res.data);
        } catch (err) { console.error(err); } finally { setLoadingHistory(false); }
    };

    const handleUpdateRate = async () => {
        try {
            await updateGestionRate({ 
                resource_id: id, 
                year: parseInt(rateForm.year), 
                month: parseInt(rateForm.month), 
                rate: parseFloat(rateForm.rate),
                comment: rateForm.comment 
            });
            await fetchFinancialData(id);
            setShowRateForm(false);
            setRateForm(prev => ({...prev, comment: ""}));
        } catch (err) { alert("Erreur maj taux"); }
    };

    const handleSaveVirement = async () => {
        if (!editingVirement) return;
        try {
            await updateVirement({ resource_id: id, period: editingVirement.period, amount: parseFloat(editingVirement.value) });
            await fetchFinancialData(id);
            setEditingVirement(null);
        } catch (err) { alert("Erreur maj virement"); }
    };

    const handleSaveAjustement = async () => {
        if (!editingAjustement) return;
        try {
            await updateAjustement({ 
                resource_id: id, 
                period: editingAjustement.period, 
                amount: parseFloat(editingAjustement.amount || 0),
                motif: editingAjustement.motif || ''
            });
            await fetchFinancialData(id);
            setEditingAjustement(null);
        } catch (err) { alert("Erreur maj ajustement"); }
    };

    const handleSaveMissionRate = async () => {
        if (!editingMissionRate) return;
        try {
            await updateMissionSpecificRate({ 
                prestation_id: editingMissionRate.id, 
                taux_specifique: editingMissionRate.value 
            });
            
            const res = await getResourceDetails(id);
            setDetails(res.data);
            
            if (financials.length > 0) {
                await fetchFinancialData(id);
            }
            
            setEditingMissionRate(null);
        } catch (err) { 
            alert("Erreur lors de la mise à jour du taux spécifique"); 
        }
    };

    const handleOpenRateHistory = async () => {
        setLoadingRateHistory(true);
        try {
            const res = await getGestionHistory(id);
            setRateHistoryData(res.data.history);
            setShowHistoryModal(true);
        } catch (err) {
            console.error("Erreur chargement historique taux", err);
            alert("Impossible de charger l'historique.");
        } finally {
            setLoadingRateHistory(false);
        }
    };

    const handleOpenExpenses = async (year, month) => {
        setLoadingExpenses(true);
        setExpenseModalInfo({ year, month, expenses: [] }); 
        try {
            const res = await getMonthExpenses(id, year, month);
            setExpenseModalInfo({ year, month, expenses: res.data });
        } catch (err) {
            console.error("Erreur chargement NDF", err);
            alert("Erreur lors du chargement des Notes de Frais.");
            setExpenseModalInfo(null);
        } finally {
            setLoadingExpenses(false);
        }
    };

    const handleSaveDeduction = async (expenseId) => {
        if (!editingExpense) return;
        try {
            await updateExpenseDeduction(expenseId, parseFloat(editingExpense.value));
            await handleOpenExpenses(expenseModalInfo.year, expenseModalInfo.month);
            await fetchFinancialData(id);
            setEditingExpense(null);
        } catch (err) {
            alert("Erreur lors de la mise à jour de la déduction");
        }
    };

    const toggleRow = (period) => setExpandedRows(prev => ({ ...prev, [period]: !prev[period] }));
    const toggleHistoryMission = (pid) => setExpandedHistory(prev => ({ ...prev, [pid]: !prev[pid] }));

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
    const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    const formatPercent = (rate) => new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 2 }).format((rate || 0) / 100);
    const isMissionActive = (s, e) => { const n = new Date(); return n >= new Date(s) && n <= new Date(e); };
    const getStatusBadgeClass = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'payée' || s.includes('paid')) return styles.badgeSuccess;
        if (s.includes('retard') || s.includes('late')) return styles.badgeDanger;
        if (s.includes('envoyée') || s.includes('sent')) return styles.badgeInfo;
        return styles.badgeWarning;
    };

    // --- Calculs Onglet 3 : Taux Réel Cumulé sur la durée de vie ---
    const { totalSoldeReel, totalSoldePrevisionnel, statsParMois } = useMemo(() => {
        // 1. Trier chronologiquement (du plus ancien au plus récent) pour calculer le cumul juste
        const sortedFinancials = [...financials].sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });

        let runningBaseGestion = 0;
        let runningFraisReel = 0;
        let tSoldeReel = 0;
        let tSoldePrevisionnel = 0;

        const mapStats = {};

        sortedFinancials.forEach(item => {
            const caPaid = item.revenue_normal_paid + item.revenue_hno_paid;
            const caTotal = item.revenue_normal + item.revenue_hno;
            
            let coutGestion = 0;
            let baseGestionLigne = 0;

            if (item.missions_details && item.missions_details.length > 0) {
                coutGestion = item.missions_details.reduce((sum, mission) => {
                    const missionBase = mission.isNDF ? 0 : (mission.detail_normal || 0);
                    baseGestionLigne += missionBase;
                    const activeRate = (mission.taux_specifique !== undefined && mission.taux_specifique !== null) 
                        ? mission.taux_specifique 
                        : item.gestion_rate;

                    return sum + (missionBase * (activeRate / 100));
                }, 0);
            } else {
                baseGestionLigne = item.revenue_normal_hors_ndf !== undefined ? item.revenue_normal_hors_ndf : item.revenue_normal;
                coutGestion = baseGestionLigne * (item.gestion_rate / 100);
            }

            const chargesPaid = item.employer_cost + coutGestion + item.expenses + item.net_transfer;
            const chargesTotal = item.employer_cost + coutGestion + item.expenses + item.net_transfer;
            
            const soldeReelMensuel = caPaid - chargesPaid + (item.ajustement || 0);

            tSoldeReel += soldeReelMensuel;
            tSoldePrevisionnel += (caTotal - chargesTotal) + (item.ajustement || 0);
            
            // 2. Accumuler les valeurs pour le taux cumulé
            runningBaseGestion += baseGestionLigne;
            const soldePreviMensuel = caTotal - chargesTotal;
            runningFraisReel += (coutGestion + soldePreviMensuel); // <-- ICI CA UTILISE LE SOLDE A VENIR (FACTURE)

            // 3. Calcul du taux à l'instant T
            const tauxCumule = runningBaseGestion > 0 ? (runningFraisReel / runningBaseGestion) * 100 : 0;

            // 4. Stocker dans le dictionnaire pour un accès facile
            mapStats[`${item.year}-${item.month}`] = {
                fraisGestion: coutGestion,
                soldeReel: soldeReelMensuel,
                tauxCumule: tauxCumule,
                caTotal: caTotal,
                caPaid: caPaid
            };
        });

        return { 
            totalSoldeReel: tSoldeReel, 
            totalSoldePrevisionnel: tSoldePrevisionnel, 
            statsParMois: mapStats 
        };
    }, [financials]);

    const handleImpersonate = () => {
        navigate(`/admin/resources/${id}/view-as`, {
            state: { impersonatedUser: { prenom: details.info.firstName, lastName: details.info.lastName, title: details.info.title } }
        });
    };

    if (loadingDetails || !details) return <div className={styles.loading}>Chargement de la fiche...</div>;

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <button onClick={() => navigate(-1)} className={styles.btnDetail} style={{marginRight: '15px'}}>← Retour</button>
                <h1 className={styles.title}>
                    {details.info.firstName} {details.info.lastName} 
                    <span style={{fontSize:'0.6em', color:'#666', marginLeft:'10px'}}>({details.info.title})</span>
                </h1>
                <button onClick={handleImpersonate} className={styles.btnAction} style={{ marginLeft: 'auto', backgroundColor: '#8e44ad', color: 'white' }}>
                    👁️ Voir vue Consultant
                </button>
            </div>

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${activeTab === 'infos' ? styles.activeTab : ''}`} onClick={() => setActiveTab('infos')}>Informations</button>
                <button className={`${styles.tab} ${activeTab === 'missions' ? styles.activeTab : ''}`} onClick={() => setActiveTab('missions')}>Missions ({details.missions.length})</button>
                <button className={`${styles.tab} ${activeTab === 'finance' ? styles.activeTab : ''}`} onClick={() => setActiveTab('finance')} style={{ color: '#e67e22', borderBottomColor: activeTab === 'finance' ? '#e67e22' : 'transparent' }}>Performance Financière</button>
                <button className={`${styles.tab} ${activeTab === 'history' ? styles.activeTab : ''}`} onClick={() => setActiveTab('history')} style={{ color: '#27ae60', borderBottomColor: activeTab === 'history' ? '#27ae60' : 'transparent' }}>Historique Paiements</button>
            </div>

            <div className={styles.tabContent} style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                
                {/* --- ONGLET INFOS --- */}
                {activeTab === 'infos' && (
                    <div className={styles.infoGrid}>
                        <div className={styles.infoItem}><label>Email</label><span>{details.info.email}</span></div>
                        <div className={styles.infoItem}><label>Manager</label><span>{details.info.mainManager || '-'}</span></div>
                        <div className={styles.infoItem}><label>Date Entrée</label><span>{formatDate(details.info.seniorityDate)}</span></div>
                        
                        <div style={{gridColumn: '1 / -1', borderTop: '1px solid #eee', margin: '10px 0'}}></div>
                        <h3 style={{gridColumn: '1 / -1', fontSize:'1rem', color:'#3498db', marginBottom:'10px'}}>Infos Mission (Déclarées)</h3>
                        
                        <div className={styles.infoItem}>
                            <label>Poste Actuel</label>
                            <span style={{fontWeight:'bold'}}>{details.info.current_poste || '-'}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <label>Client Final</label>
                            <span>{details.info.final_client || '-'}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <label>ESN / Intermédiaire</label>
                            <span>{details.info.esn_name || 'Aucune'}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <label>TJM Actuel (Déclaré)</label>
                            <span style={{color: '#27ae60', fontWeight:'bold'}}>{details.info.current_tjm ? formatCurrency(details.info.current_tjm) : '-'}</span>
                        </div>
                        
                        <div style={{gridColumn: '1 / -1', borderTop: '1px solid #eee', margin: '10px 0'}}></div>
                        <div className={styles.infoItem}><label>Taux Gestion Base</label><span>{details.info.actualGestion}%</span></div>
                        <div className={styles.infoItem}><label>Pays</label><span>{details.info.country}</span></div>
                    </div>
                )}

                {/* --- ONGLET MISSIONS --- */}
                {activeTab === 'missions' && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Partenaire</th>
                                <th>Début</th>
                                <th>Fin</th>
                                <th>TJM Vendu</th>
                                <th>Taux Spécifique</th>
                                <th>Statut</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {details.missions.map((m, idx) => {
                                const active = isMissionActive(m.startDate, m.endDate);
                                const isEditingRate = editingMissionRate && editingMissionRate.id === m.prestation_id;
                                
                                return (
                                    <tr key={idx}>
                                        <td className={styles.fwBold}>{m.clientName}</td>
                                        <td>{formatDate(m.startDate)}</td>
                                        <td>{formatDate(m.endDate)}</td>
                                        <td>{m.TJM}€</td>
                                        
                                        <td style={{ backgroundColor: isEditingRate ? '#fff' : 'transparent' }}>
                                            {isEditingRate ? (
                                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                                    <input 
                                                        type="number" 
                                                        step="0.1"
                                                        value={editingMissionRate.value || ''} 
                                                        onChange={(e)=>setEditingMissionRate({...editingMissionRate, value: e.target.value})} 
                                                        placeholder="Global"
                                                        style={{width: '70px', padding:'4px', border:'1px solid #3498db', borderRadius:'4px'}} 
                                                        autoFocus 
                                                    />
                                                    <button onClick={handleSaveMissionRate} style={{border:'none', background:'none', cursor:'pointer'}}>✅</button>
                                                    <button onClick={()=>setEditingMissionRate(null)} style={{border:'none', background:'none', cursor:'pointer'}}>❌</button>
                                                </div>
                                            ) : (
                                                <div 
                                                    onClick={() => setEditingMissionRate({id: m.prestation_id, value: m.taux_specifique})} 
                                                    style={{cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center'}}
                                                    title="Cliquer pour forcer un taux spécifique sur cette mission"
                                                >
                                                    <span style={{ fontWeight: m.taux_specifique !== null ? 'bold' : 'normal', color: m.taux_specifique !== null ? '#e67e22' : '#95a5a6' }}>
                                                        {m.taux_specifique !== null ? `${m.taux_specifique}%` : 'Défaut'}
                                                    </span>
                                                    <span style={{fontSize:'0.8em', opacity:0.5, marginLeft:'5px'}}>✎</span>
                                                </div>
                                            )}
                                        </td>

                                        <td><span className={active ? styles.badgeSuccess : styles.badgeInfo}>{active ? 'Active' : 'Terminée'}</span></td>
                                        <td><button className={styles.btnDetail} onClick={() => setSelectedMissionId(m.prestation_id)}>Voir</button></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}

                {/* --- ONGLET FINANCE --- */}
                {activeTab === 'finance' && (
                    loadingFinance || !activeInfo ? <div className={styles.loading}>Calcul...</div> : (
                        <div>
                            {/* Résumé Header */}
                             <div className={styles.infoGrid} style={{marginBottom:'20px', background:'#f8f9fa', padding:'15px', borderRadius:'8px', alignItems:'center'}}>
                                <div className={styles.infoItem}>
                                    <label>Info Actuelle</label>
                                    <span><strong>Partenaire:</strong> {activeInfo.clients}</span>
                                    <span><strong>TJM:</strong> {activeInfo.tjm}</span>
                                    
                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                        <span><strong>Taux Appliqué:</strong> {formatPercent(activeInfo.current_gestion_rate)}</span>
                                        <button 
                                            onClick={handleOpenRateHistory}
                                            disabled={loadingRateHistory}
                                            style={{
                                                fontSize:'0.75rem', padding:'2px 10px', 
                                                border:'1px solid #3498db', backgroundColor:'white', 
                                                color:'#3498db', borderRadius:'12px', cursor:'pointer',
                                                display: 'flex', alignItems:'center', gap:'4px'
                                            }}
                                            title="Voir l'historique des changements"
                                        >
                                            {loadingRateHistory ? '...' : '📜 Détail'}
                                        </button>
                                    </div>

                                    {!showRateForm && <button className={styles.btnAction} onClick={() => setShowRateForm(true)} style={{marginTop: '10px', fontSize:'0.8rem'}}>✏️ Modifier le taux futur</button>}
                                </div>
                                <div style={{ flex: 1, display: 'flex', gap: '20px', marginLeft: '20px', paddingLeft: '20px', borderLeft: '4px solid #3498db' }}>
                                    <div className={styles.infoItem}>
                                        <label>Solde Réel (Encaissé)</label>
                                        <span style={{fontSize:'1.5rem', fontWeight:'bold', color: totalSoldeReel >= 0 ? '#27ae60' : '#e74c3c'}}>{formatCurrency(totalSoldeReel)}</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <label>Solde à Venir</label>
                                        <span style={{fontSize:'1.5rem', fontWeight:'bold', color: '#3498db'}}>{formatCurrency(totalSoldePrevisionnel)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Formulaire modif taux */}
                            {showRateForm && (
                                <div className={styles.formContainer}>
                                    <div className={styles.inlineForm} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                                        <h4 style={{marginBottom:'15px', color:'#2c3e50'}}>Modifier le Taux de Gestion</h4>
                                        
                                        <div style={{display:'flex', gap:'15px', flexWrap:'wrap', width:'100%'}}>
                                            <div className={styles.formGroup}>
                                                <label>À partir de (Mois) :</label>
                                                <select value={rateForm.month} onChange={(e)=>setRateForm({...rateForm, month:e.target.value})} className={styles.inputSelect}>
                                                    {Array.from({length:12},(_,i)=>i+1).map(m=><option key={m} value={m}>{m}</option>)}
                                                </select>
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Année :</label>
                                                <select value={rateForm.year} onChange={(e)=>setRateForm({...rateForm, year:e.target.value})} className={styles.inputSelect}>
                                                    {[2023,2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
                                                </select>
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label>Nouveau Taux (%) :</label>
                                                <input type="number" step="0.1" value={rateForm.rate} onChange={(e)=>setRateForm({...rateForm, rate:e.target.value})} className={styles.inputNumber} />
                                            </div>
                                        </div>

                                        <div className={styles.formGroup} style={{width: '100%', marginTop:'10px'}}>
                                            <label>Pourquoi ce changement ? (Commentaire)</label>
                                            <textarea 
                                                rows="2" 
                                                value={rateForm.comment} 
                                                onChange={(e)=>setRateForm({...rateForm, comment:e.target.value})}
                                                placeholder="Ex: Nouvelle négociation..."
                                                style={{width:'100%', padding:'8px', borderRadius:'4px', border:'1px solid #ccc', fontFamily:'inherit'}}
                                            />
                                        </div>

                                        <div style={{fontSize:'0.85rem', color:'#7f8c8d', margin:'10px 0', fontStyle:'italic'}}>
                                            ℹ️ Ce taux s'appliquera à ce mois <strong>et deviendra la règle pour les mois suivants</strong>.
                                        </div>

                                        <div className={styles.formActions} style={{alignSelf: 'flex-end'}}>
                                            <button onClick={()=>setShowRateForm(false)} className={styles.btnCancel}>Annuler</button>
                                            <button onClick={handleUpdateRate} className={styles.btnSave}>Valider</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TABLEAU PRINCIPAL */}
                            <div style={{overflowX: 'auto', marginTop: '20px'}}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr style={{background:'#2c3e50', color:'white'}}>
                                            <th>Période</th><th>Partenaire</th><th>CA Facturé <small>(Hors HNO)</small></th><th>CA Encaissé</th><th>Coût Empl.</th><th>Frais Gestion</th><th>Taux Cumulé</th><th>Ajust. Except.</th><th>Virement Net</th><th>NDF</th><th>HNO</th><th>Total</th><th>Reste Mensuel</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {financials.map((item, idx) => {
                                            // On récupère les valeurs calculées dans le useMemo pour CE mois-ci
                                            const stats = statsParMois[`${item.year}-${item.month}`] || {};
                                            const { fraisGestion = 0, soldeReel = 0, tauxCumule = 0, caTotal = 0, caPaid = 0 } = stats;

                                            const hnoAmount = item.revenue_hno || 0;
                                            const customTotal = item.employer_cost + fraisGestion + item.net_transfer + item.expenses - (item.ajustement || 0);
                                            
                                            const isPaid = Math.abs(caPaid - caTotal) < 1;
                                            const hasDetails = item.missions_details && item.missions_details.length > 0;
                                            const isExpanded = expandedRows[item.period];
                                            const isEditing = editingVirement && editingVirement.period === item.period;

                                            return (
                                                <React.Fragment key={idx}>
                                                    <tr className={isExpanded ? styles.expandedRow : ''}>
                                                        <td style={{fontWeight:'bold', cursor: hasDetails ? 'pointer' : 'default'}} onClick={() => hasDetails && toggleRow(item.period)}>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <span style={{ display: 'inline-block', width: '20px', color: hasDetails ? 'inherit' : '#ccc', visibility: hasDetails ? 'visible' : 'hidden', marginRight: '5px' }}>
                                                                    {isExpanded ? '▼' : '▶'}
                                                                </span>
                                                                <span>{item.period}</span>
                                                            </div>
                                                        </td>
                                                        <td>{hasDetails ? item.missions_details.map(m => m.clientName).join(', ') : '-'}</td>
                                                        <td>{formatCurrency(item.revenue_normal)}</td>
                                                        <td style={{
                                                            fontWeight: 'bold',
                                                            color: caTotal === 0 ? '#e74c3c' : (isPaid ? '#27ae60' : '#f39c12')
                                                        }}>
                                                            {formatCurrency(caPaid)}
                                                        </td>
                                                        <td>{formatCurrency(item.employer_cost)}</td>
                                                        <td>
                                                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                                                <span>{formatCurrency(fraisGestion)}</span>
                                                                {item.missions_details && item.missions_details.some(m => m.taux_specifique !== undefined && m.taux_specifique !== null) ? (
                                                                    <small style={{fontSize: '0.7em', color: '#e67e22'}}>(Taux multiples)</small>
                                                                ) : (
                                                                    <small style={{fontSize: '0.7em', color: '#666'}}>({item.gestion_rate}%)</small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{
                                                            fontWeight:'bold', 
                                                            color: Math.abs(soldeReel) < 0.1 ? '#2c3e50' : (soldeReel > 0 ? '#27ae60' : '#e74c3c')
                                                        }}>
                                                            {formatPercent(tauxCumule)}
                                                        </td>
                                                        <td className={editingAjustement?.period === item.period ? styles.editableCell : styles.readOnlyCell} style={{backgroundColor: editingAjustement?.period === item.period ? '#fff' : 'transparent', verticalAlign:'top'}}>
                                                            {editingAjustement?.period === item.period ? (
                                                                <div style={{display:'flex', flexDirection:'column', gap:'5px'}}>
                                                                    <input 
                                                                        type="number" 
                                                                        step="0.01"
                                                                        value={editingAjustement.amount} 
                                                                        onChange={(e)=>setEditingAjustement({...editingAjustement, amount:e.target.value})} 
                                                                        placeholder="Montant (+ ou -)"
                                                                        style={{width: '90px', padding:'4px', border:'1px solid #3498db', borderRadius:'4px'}} 
                                                                        autoFocus 
                                                                    />
                                                                    <input 
                                                                        type="text" 
                                                                        value={editingAjustement.motif} 
                                                                        onChange={(e)=>setEditingAjustement({...editingAjustement, motif:e.target.value})} 
                                                                        placeholder="Motif (ex: Prime)"
                                                                        style={{width: '90px', padding:'4px', border:'1px solid #3498db', borderRadius:'4px'}} 
                                                                    />
                                                                    <div style={{display:'flex', gap:'5px'}}>
                                                                        <button onClick={handleSaveAjustement} style={{border:'none', background:'none', cursor:'pointer'}}>✅</button>
                                                                        <button onClick={()=>setEditingAjustement(null)} style={{border:'none', background:'none', cursor:'pointer'}}>❌</button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div onClick={() => setEditingAjustement({period: item.period, amount: item.ajustement_manuel ?? (item.ajustement || 0), motif: item.motif_ajustement_manuel ?? (item.motif_ajustement || '')})} style={{cursor:'pointer'}}>
                                                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                                        <span style={{fontWeight:'bold', color: item.ajustement > 0 ? '#27ae60' : (item.ajustement < 0 ? '#c0392b' : '#7f8c8d')}}>
                                                                            {item.ajustement ? formatCurrency(item.ajustement) : '-'}
                                                                        </span>
                                                                        <span style={{fontSize:'0.8em', opacity:0.5, marginLeft:'5px'}}>✎</span>
                                                                    </div>
                                                                    {item.motif_ajustement && (
                                                                        <div style={{fontSize:'0.7rem', color:'#95a5a6', fontStyle:'italic', marginTop:'4px', lineHeight:'1.1'}}>
                                                                            {item.motif_ajustement}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className={isEditing ? styles.editableCell : styles.readOnlyCell} style={{backgroundColor: isEditing ? '#fff' : '#f8f9fa', fontWeight:'bold'}}>
                                                            {isEditing ? (
                                                                <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                                                    <input type="number" value={editingVirement.value} onChange={(e)=>setEditingVirement({...editingVirement, value:e.target.value})} className={styles.editableInput} style={{width: '70px', padding:'4px', border:'1px solid #3498db', borderRadius:'4px'}} autoFocus />
                                                                    <button onClick={handleSaveVirement} style={{border:'none', background:'none', cursor:'pointer'}}>✅</button>
                                                                    <button onClick={()=>setEditingVirement(null)} style={{border:'none', background:'none', cursor:'pointer'}}>❌</button>
                                                                </div>
                                                            ) : (
                                                                <div onClick={() => setEditingVirement({period: item.period, value: item.net_transfer})} style={{cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                                                    {formatCurrency(item.net_transfer)} 
                                                                    <span style={{fontSize:'0.8em', opacity:0.5, marginLeft:'5px'}}>✎</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        
                                                        <td 
                                                            onClick={() => handleOpenExpenses(item.year, item.month)} 
                                                            style={{cursor: 'pointer', color: '#3498db', textDecoration: 'underline'}}
                                                            title="Gérer les déductions NDF"
                                                        >
                                                            {formatCurrency(item.expenses)}
                                                        </td>
                                                        
                                                        <td>{formatCurrency(hnoAmount)}</td>
                                                        <td style={{fontWeight:'bold', color: '#2c3e50'}}>{formatCurrency(customTotal)}</td>
                                                        <td style={{fontWeight:'bold', color: soldeReel >= 0 ? '#27ae60' : '#c0392b'}}>{formatCurrency(soldeReel)}</td>
                                                    </tr>
                                                    
                                                    {isExpanded && hasDetails && (
                                                        <tr className={styles.detailsRow}>
                                                            <td colSpan="13">
                                                                <div className={styles.detailsContainer} style={{padding:'10px 20px', backgroundColor:'#f1f2f6'}}>
                                                                    <p className={styles.detailsTitle} style={{fontWeight:'bold', marginBottom:'10px'}}>Détail par Facture :</p>
                                                                    <table className={styles.subTable} style={{width:'100%', background:'white', fontSize:'0.9rem'}}>
                                                                        <thead>
                                                                            <tr style={{borderBottom:'1px solid #ddd'}}>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>Partenaire</th>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>Statut</th>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>Hors HNO (HT / Qty)</th>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>HNO (HT / Qty)</th>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>Total HT</th>
                                                                                <th style={{padding:'8px', textAlign:'left'}}>Total TTC</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {item.missions_details.map((mission, subIdx) => (
                                                                                <tr key={subIdx} style={{borderBottom:'1px solid #eee'}}>
                                                                                    <td style={{padding:'8px'}}>
                                                                                        {mission.clientName}
                                                                                        {mission.isNDF ? <span style={{marginLeft:'8px', backgroundColor:'#3498db', color:'white', padding:'2px 6px', borderRadius:'4px', fontSize:'0.7rem', fontWeight:'bold'}}>NDF</span> : null}
                                                                                    </td>
                                                                                    <td style={{padding:'8px'}}><span className={`${styles.badge} ${getStatusBadgeClass(mission.stateFacture)}`}>{mission.stateFacture || 'Inconnu'}</span></td>
                                                                                    <td style={{padding:'8px'}}>
                                                                                        <div>{formatCurrency(mission.detail_normal || 0)}</div>
                                                                                        {mission.isNDF ? (
                                                                                            <small style={{color: '#3498db', fontWeight: 'bold'}}>Frais refacturés</small>
                                                                                        ) : (
                                                                                            <small style={{color:'#666'}}>{mission.detail_qty_normal || 0} jours</small>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{padding:'8px'}}>
                                                                                        {!mission.isNDF ? (
                                                                                            <>
                                                                                                <div style={{color:'#27ae60'}}>{formatCurrency(mission.detail_hno || 0)}</div>
                                                                                                <small style={{color:'#666'}}>{mission.detail_qty_hno || 0} jours</small>
                                                                                            </>
                                                                                        ) : (
                                                                                            <div style={{color: '#95a5a6'}}>-</div>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{padding:'8px', fontWeight:'bold'}}>{formatCurrency((mission.detail_normal || 0) + (mission.detail_hno || 0))}</td>
                                                                                    <td style={{padding:'8px', fontWeight:'bold', color:'#7f8c8d'}}>{formatCurrency((mission.detail_ttc_normal || 0) + (mission.detail_ttc_hno || 0))}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                )}

                {/* --- 4EME ONGLET : HISTORIQUE PAIEMENTS --- */}
                {activeTab === 'history' && (
                    loadingHistory || !paidHistory ? <div className={styles.loading}>Chargement de l'historique...</div> : (
                        <div style={{animation: 'fadeIn 0.3s'}}>
                            {/* Résumé Global */}
                            <div style={{display:'flex', gap:'20px', marginBottom:'25px'}}>
                                <div style={{flex:1, background:'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', color:'white', padding:'20px', borderRadius:'10px', boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
                                    <div style={{fontSize:'0.9rem', opacity:0.9}}>Total Historique Encaissé (HT)</div>
                                    <div style={{fontSize:'2rem', fontWeight:'bold'}}>{formatCurrency(paidHistory.summary.total_global_ht)}</div>
                                </div>
                                <div style={{flex:1, background:'white', border:'1px solid #eee', padding:'20px', borderRadius:'10px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)'}}>
                                    <div style={{fontSize:'0.9rem', color:'#7f8c8d'}}>Total TTC Encaissé</div>
                                    <div style={{fontSize:'2rem', fontWeight:'bold', color:'#2c3e50'}}>{formatCurrency(paidHistory.summary.total_global_ttc)}</div>
                                </div>
                                <div style={{flex:1, background:'white', border:'1px solid #eee', padding:'20px', borderRadius:'10px', boxShadow:'0 4px 6px rgba(0,0,0,0.05)'}}>
                                    <div style={{fontSize:'0.9rem', color:'#7f8c8d'}}>Missions Soldées/Payées</div>
                                    <div style={{fontSize:'2rem', fontWeight:'bold', color:'#3498db'}}>{paidHistory.summary.total_missions_paid}</div>
                                </div>
                            </div>

                            {/* Liste des Missions (Accordéon) */}
                            {paidHistory.history.length === 0 ? (
                                <div style={{textAlign:'center', padding:'40px', color:'#7f8c8d'}}>Aucune facture payée trouvée pour ce consultant.</div>
                            ) : (
                                <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                                    {paidHistory.history.map((mission) => {
                                        const isExpanded = expandedHistory[mission.prestation_id];
                                        return (
                                            <div key={mission.prestation_id} style={{border:'1px solid #e1e4e8', borderRadius:'8px', overflow:'hidden', backgroundColor:'white'}}>
                                                <div 
                                                    onClick={() => toggleHistoryMission(mission.prestation_id)}
                                                    style={{
                                                        padding:'15px 20px', cursor:'pointer', 
                                                        background: isExpanded ? '#f8f9fa' : 'white',
                                                        display:'flex', justifyContent:'space-between', alignItems:'center',
                                                        borderBottom: isExpanded ? '1px solid #e1e4e8' : 'none',
                                                        transition: 'background 0.2s'
                                                    }}
                                                >
                                                    <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                                                        <div style={{width:'30px', height:'30px', borderRadius:'50%', background: isExpanded ? '#3498db' : '#ecf0f1', color: isExpanded ? 'white' : '#7f8c8d', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold'}}>
                                                            {isExpanded ? '▼' : '▶'}
                                                        </div>
                                                        <div>
                                                            <div style={{fontWeight:'bold', fontSize:'1.1rem', color:'#2c3e50'}}>{mission.clientName}</div>
                                                            <div style={{fontSize:'0.85rem', color:'#7f8c8d'}}>
                                                                Du {formatDate(mission.startDate)} au {formatDate(mission.endDate)} • TJM: {mission.tjm}€
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{textAlign:'right'}}>
                                                        <div style={{fontWeight:'bold', color:'#27ae60', fontSize:'1.2rem'}}>
                                                            {formatCurrency(mission.total_mission_paid_ht)}
                                                        </div>
                                                        <div style={{fontSize:'0.8rem', color:'#95a5a6'}}>
                                                            {mission.total_days_paid} jours facturés & payés
                                                        </div>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div style={{padding:'0', animation:'slideDown 0.3s'}}>
                                                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem'}}>
                                                            <thead style={{background:'#f1f2f6', color:'#666'}}>
                                                                <tr>
                                                                    <th style={{padding:'12px 20px', textAlign:'left'}}>Date Facture</th>
                                                                    <th style={{padding:'12px 20px', textAlign:'left'}}>Date Paiement</th>
                                                                    <th style={{padding:'12px 20px', textAlign:'left'}}>Jours</th>
                                                                    <th style={{padding:'12px 20px', textAlign:'right'}}>Montant HT</th>
                                                                    <th style={{padding:'12px 20px', textAlign:'right'}}>Montant TTC</th>
                                                                    <th style={{padding:'12px 20px', textAlign:'center'}}>Statut</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {mission.invoices.map((inv) => (
                                                                    <tr key={inv.facture_id} style={{borderBottom:'1px solid #eee'}}>
                                                                        <td style={{padding:'12px 20px'}}>{formatDate(inv.date)}</td>
                                                                        <td style={{padding:'12px 20px', color:'#27ae60'}}>{formatDate(inv.paymentDate)}</td>
                                                                        <td style={{padding:'12px 20px'}}>{inv.days}</td>
                                                                        <td style={{padding:'12px 20px', textAlign:'right', fontWeight:'500'}}>{formatCurrency(inv.amount_ht)}</td>
                                                                        <td style={{padding:'12px 20px', textAlign:'right', color:'#7f8c8d'}}>{formatCurrency(inv.amount_ttc)}</td>
                                                                        <td style={{padding:'12px 20px', textAlign:'center'}}>
                                                                            <span className={styles.badgeSuccess} style={{fontSize:'0.75rem', padding:'4px 8px'}}>PAYÉE</span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )
                )}

            </div>

            {selectedMissionId && <MissionDetailsModal missionId={selectedMissionId} onClose={() => setSelectedMissionId(null)} />}
            {showHistoryModal && <RateHistoryModal history={rateHistoryData} onClose={() => setShowHistoryModal(false)} />}
            
            {/* MODAL DES NOTES DE FRAIS */}
            {expenseModalInfo && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', 
                    justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: 'white', padding: '25px', borderRadius: '8px', 
                        width: '600px', maxWidth: '90%', boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{marginTop: 0, color: '#2c3e50', borderBottom: '2px solid #eee', paddingBottom: '10px'}}>
                            Détail NDF - {expenseModalInfo.month}/{expenseModalInfo.year}
                        </h3>
                        
                        {loadingExpenses ? (
                            <p style={{textAlign: 'center', padding: '20px'}}>Chargement des NDF...</p>
                        ) : (
                            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                                {expenseModalInfo.expenses.length === 0 ? (
                                    <p style={{textAlign: 'center', color: '#7f8c8d'}}>Aucune note de frais pour ce mois.</p>
                                ) : (
                                    expenseModalInfo.expenses.map(exp => (
                                        <div key={exp.expense_id} style={{ padding: '15px 10px', borderBottom: '1px solid #f1f2f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{fontWeight: 'bold'}}>NDF #{exp.expense_id}</div>
                                                <div style={{fontSize: '0.85rem', color: '#7f8c8d'}}>Statut : {exp.stateExpense}</div>
                                                <div style={{fontSize: '0.9rem', marginTop: '5px'}}>Total base : {formatCurrency(exp.totalAmount)}</div>
                                            </div>
                                            
                                            {editingExpense?.id === exp.expense_id ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa', padding: '10px', borderRadius: '6px' }}>
                                                    <label style={{fontSize: '0.85rem', fontWeight: 'bold'}}>Déduction (-):</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01"
                                                        value={editingExpense.value} 
                                                        onChange={(e) => setEditingExpense({...editingExpense, value: e.target.value})}
                                                        style={{ width: '80px', padding: '5px', border: '1px solid #3498db', borderRadius: '4px' }}
                                                        autoFocus
                                                    />
                                                    <button onClick={() => handleSaveDeduction(exp.expense_id)} style={{border:'none', background:'none', cursor:'pointer', fontSize: '1.2rem'}}>✅</button>
                                                    <button onClick={() => setEditingExpense(null)} style={{border:'none', background:'none', cursor:'pointer', fontSize: '1.2rem'}}>❌</button>
                                                </div>
                                            ) : (
                                                <div 
                                                    style={{ cursor: 'pointer', color: '#e67e22', fontWeight: 'bold', background: '#fdf3e7', padding: '8px 12px', borderRadius: '6px', border: '1px dashed #fad390' }} 
                                                    onClick={() => setEditingExpense({id: exp.expense_id, value: exp.deduction_manuelle})}
                                                    title="Cliquez pour modifier la déduction"
                                                >
                                                    Déduction : -{formatCurrency(exp.deduction_manuelle)} ✎
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        
                        <div style={{textAlign: 'right', marginTop: '20px'}}>
                            <button 
                                onClick={() => setExpenseModalInfo(null)} 
                                style={{padding: '8px 16px', background: '#34495e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourceDetailsPage;